import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { PublicShell } from './PublicLayout'

const LegalArticle = styled.article`
  max-width: 760px;
  margin: 0 auto;
  padding: ${minimalVars.space.xl} ${minimalVars.space.md} 80px;

  h1 {
    font-family: ${minimalVars.typography.displayFamily};
    /* Policy titles keep their own scale: displaySize lifts the floor from 30px to 40px. */
    font-size: clamp(1.9rem, 4vw, 2.5rem);
    letter-spacing: -0.025em;
    margin: 0 0 6px;
    color: ${minimalVars.color.textPrimary};
  }
  h2 {
    /* Section heads stay at 1.15rem */
    font-size: 1.15rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    margin: 34px 0 10px;
    color: ${minimalVars.color.textPrimary};
  }
  p,
  li {
    color: ${minimalVars.color.textSecondary};
    line-height: 1.62;
  }
  p {
    margin: 0 0 13px;
  }
  ul {
    margin: 0 0 13px;
    padding-left: 22px;
  }
  li {
    margin-bottom: 7px;
  }
  strong {
    color: ${minimalVars.color.textPrimary};
  }
  a {
    color: ${minimalVars.color.brand};
  }
`

const Updated = styled.p`
  color: ${minimalVars.color.textTertiary} !important;
  font-size: ${minimalVars.typography.captionSize};
  margin-bottom: ${minimalVars.space.md} !important;
`

const Callout = styled.aside`
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-left: 3px solid ${minimalVars.color.brand};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurface};
  padding: 15px 18px;
  margin: 22px 0;
  p:last-child {
    margin-bottom: 0;
  }
`

/**
 * The date these documents last changed.
 *
 * Deliberately a constant rather than `new Date()`: a policy page that claims
 * to have been updated today, every day, is telling the reader nothing — and
 * the one thing a person checks on a privacy notice is whether it changed since
 * they last agreed to it. Bump this by hand when the text changes.
 */
const LAST_UPDATED = '3 October 2026'

/** The operating entity. Replace with the registered company details. */
const CONTACT_EMAIL = 'privacy@choosechow.com'
const SUPPORT_EMAIL = 'support@choosechow.com'

const LegalShell = ({ children }: { children: ReactNode }) => (
  <PublicShell>
    <LegalArticle>{children}</LegalArticle>
  </PublicShell>
)

export const PrivacyPage = () => (
  <LegalShell>
    <h1>Privacy policy</h1>
    <Updated>Last updated {LAST_UPDATED}</Updated>

    <p>
      This notice explains what personal data ChooseChow collects when you order food or sell
      through the platform, why we hold it, who we share it with, and the rights you have over it
      under the <strong>Nigeria Data Protection Act 2023</strong>.
    </p>

    <Callout>
      <p>
        <strong>The short version.</strong> We collect what an order needs: who you are, where the
        food is going, and what you paid. We share your address and phone number with the kitchen
        cooking your food and the rider bringing it, because they cannot deliver without them. We do
        not sell your data.
      </p>
    </Callout>

    <h2>Who is responsible for your data</h2>
    <p>
      ChooseChow is the data controller for the personal data described here. You can reach us about
      anything on this page at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
    </p>

    <h2>What we collect</h2>
    <ul>
      <li>
        <strong>Account details</strong> — your name, email address, phone number and password (which
        we store only as a one-way hash, never as text we could read).
      </li>
      <li>
        <strong>Location</strong> — the delivery addresses you save, and their coordinates. When you
        tap "use my location" we read your device's precise location once to fill in the address.
        Coordinates match you with nearby kitchens and guide the rider to your door; your address stays
        on your order history.
      </li>
      <li>
        <strong>Order history</strong> — what you ordered, from which kitchen, when, and what it cost.
      </li>
      <li>
        <strong>Dietary information you choose to give us</strong> — allergies and dietary
        preferences, so a kitchen can cook around them. You do not have to provide this, and you can
        remove it at any time.
      </li>
      <li>
        <strong>Payment and wallet records</strong> — your purchase history, and the amount, currency,
        status and reference of each payment, wallet top-up, refund and payout. Customer card details
        and chef bank details are processed through Paystack. ChooseChow does not store full payment card
        numbers.
      </li>
      <li>
        <strong>Photos</strong> — an optional profile photo, and for kitchens, photos of the dishes
        they sell.
      </li>
      <li>
        <strong>Messages</strong> — what you send to a kitchen, a rider or our support team about an
        order.
      </li>
      <li>
        <strong>Rider location</strong> — while a rider is on an active delivery, so you can see where
        your food is. This is not collected when a rider is off duty.
      </li>
      <li>
        <strong>Hygiene reports</strong> — if you report a hygiene problem with an order, the reason,
        any description you give, and which order it was.
      </li>
      <li>
        <strong>Technical logs</strong> — our servers record requests and errors (time, address and
        type of request, and error details) to keep the service secure and working. We do not use
        third-party analytics, advertising or crash-reporting tools in the app.
      </li>
    </ul>
    <p>
      We do not read your contact list or address book. The only phone number we hold is the one you
      give us for your own account.
    </p>

    <h2>If you sell on ChooseChow: verification</h2>
    <p>
      Every kitchen is verified before it can sell, and re-verified every 6 months. To verify your
      kitchen, we collect:
    </p>
    <ul>
      <li>identity details: a government ID (NIN slip or driver's licence) and a selfie;</li>
      <li>contact information: your phone number, confirmed by a one-time code or a call;</li>
      <li>kitchen and food-safety details: photos, video of your kitchen, and food-handler certificates;</li>
      <li>bank account details: a bank account in your verified name to configure a Paystack subaccount and receive settlements;</li>
      <li>your acceptance of our hygiene standards, with its timestamp and version;</li>
      <li>inspection records, hygiene reports about your kitchen, and review outcomes.</li>
    </ul>
    <p>
      We use these details to verify chefs, set up payment subaccounts, send settlements, prevent fraud,
      and meet legal obligations.
    </p>
    <p>
      These files are stored privately. They are never published, never shown to customers, and never
      shared with other kitchens. Customers see only your badge: Verified, Hygiene Certified or Top
      Kitchen. We may pause your kitchen while a hygiene report is reviewed; the reporting customer's
      contact details are not shared with you.
    </p>

    <h2>Why we hold it, and on what basis</h2>
    <ul>
      <li>
        <strong>To perform our contract with you</strong> — taking, cooking, delivering and paying for
        an order, and remitting settlements to kitchens. Without this data there is no order.
      </li>
      <li>
        <strong>With your consent</strong> — allergy and dietary information, and your device location
        when you tap "use my location". You can withdraw consent at any time; the rest of the service
        continues to work without it.
      </li>
      <li>
        <strong>For our legitimate interests</strong> — verifying kitchens, preventing fraud,
        investigating food-safety and hygiene reports, and keeping the service working.
      </li>
      <li>
        <strong>To comply with the law</strong> — meeting regulatory duties and keeping financial, tax,
        and transaction records for as long as Nigerian law requires.
      </li>
    </ul>

    <h2>Who we share it with</h2>
    <ul>
      <li>
        <strong>The kitchen cooking your order</strong> — your first name, your order, any dietary
        note attached to it, and your delivery address.
      </li>
      <li>
        <strong>The rider delivering it</strong> — your first name, phone number, delivery address and
        its location on the map, for the duration of that delivery.
      </li>
      <li>
        <strong>Our payment processor (Paystack)</strong> — to process payments and settlements. We share
        the information needed for transactions, including customer name, email address, phone number,
        order amount, and chef bank details. Paystack handles this data under its own privacy terms.
      </li>
      <li>
        <strong>Our SMS provider</strong> — a kitchen's phone number, only to send its verification
        code.
      </li>
      <li>
        <strong>Service providers who host and operate the platform</strong>, under contract and only
        on our instructions.
      </li>
      <li>
        <strong>Law enforcement or regulators</strong>, where we are legally required to.
      </li>
    </ul>
    <p>
      We do not sell personal data, we do not share it with advertisers, and we do not use it for
      advertising or marketing.
    </p>

    <h2>Public information</h2>
    <p>
      If you sell on ChooseChow, your kitchen name, trading area, cuisine, opening hours, menu,
      prices and photographs are published on a public page and are visible to anyone, including
      search engines. Reviews you write are published with the display name on your account. Nothing
      else about a customer account is ever made public.
    </p>

    <h2>How long we keep it</h2>
    <p>
      Order, payment, and payout records are kept for 6 years from the end of the financial year in which
      the transaction occurred, or as required by law. Account details are kept while your account is open.
      When you delete your account we remove your profile, saved addresses and dietary information, and
      retain only the transaction records we are obliged to keep — with your identifying details removed
      where that is possible without breaking the record. Kitchen verification records, inspection records,
      and hygiene reports are retained for 6 years or as required by law to evidence compliance; they are
      never published.
    </p>

    <h2>Your rights</h2>
    <p>Under the Nigeria Data Protection Act 2023 you can ask us to:</p>
    <ul>
      <li>give you a copy of the personal data we hold about you (access);</li>
      <li>correct anything that is wrong (rectification);</li>
      <li>delete your data, where we are not required to keep it (erasure);</li>
      <li>stop processing it for a particular purpose, or object to that processing;</li>
      <li>hand your data to you or to another service in a portable form;</li>
      <li>withdraw a consent you previously gave.</li>
    </ul>
    <p>
      Write to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will respond within 30
      days. If you are not satisfied with our answer you can complain to the Nigeria Data Protection
      Commission.
    </p>

    <h2>Security</h2>
    <p>
      We use reasonable technical and organisational measures to protect payment and identity data.
      Traffic between your device and ChooseChow is encrypted in transit (HTTPS/TLS). We do not store
      full payment card numbers. Passwords and phone verification codes are stored only as one-way hashes.
      Kitchen verification documents are served only to their owner and our review team, and are never
      cached by browsers. Access to production data is restricted to the people who need it to operate
      the service. No system is perfectly secure, and we will tell you and the Commission without
      undue delay if a breach is likely to put your rights at risk.
    </p>

    <h2>Children</h2>
    <p>
      ChooseChow is not intended for anyone under 18, and we do not knowingly collect data from
      children. If you believe a child has given us personal data, contact us and we will remove it.
    </p>

    <h2>Changes</h2>
    <p>
      If we change this notice we will update the date at the top and, where the change materially
      affects you, tell you in the app before it takes effect.
    </p>
  </LegalShell>
)

export const TermsPage = () => (
  <LegalShell>
    <h1>Terms of service</h1>
    <Updated>Last updated {LAST_UPDATED}</Updated>

    <p>
      These terms apply when you use ChooseChow — whether you are ordering food, cooking it, or
      delivering it. By creating an account you agree to them.
    </p>

    <Callout>
      <p>
        <strong>What ChooseChow is.</strong> ChooseChow Limited ("ChooseChow") operates a marketplace.
        The kitchens on ChooseChow are independent businesses; they cook the food and are responsible
        for it. ChooseChow is not the seller of the food. We provide the platform that connects you to
        them, arranges delivery, and facilitates payment processing through Paystack, a licensed
        payment service provider.
      </p>
    </Callout>

    <h2>Your account</h2>
    <ul>
      <li>You must be 18 or older to hold an account.</li>
      <li>Give accurate details and keep your password to yourself.</li>
      <li>You are responsible for what happens under your account.</li>
    </ul>

    <h2>Ordering</h2>
    <ul>
      <li>
        Prices are set by the kitchen and shown before you pay. Delivery is quoted separately at
        checkout.
      </li>
      <li>
        An order is a contract with the kitchen, formed when they accept it. Until then it can be
        declined — a kitchen may be at capacity or out of an ingredient — and you are refunded in
        full.
      </li>
      <li>
        Food is cooked after you order, so an order cannot be cancelled once the kitchen has started
        cooking. Before that, cancel in the app for a full refund.
      </li>
      <li>
        Delivery times are estimates. Traffic and weather affect them, and we will keep you updated
        rather than pretend otherwise.
      </li>
      <li>
        Deliveries are confirmed in two steps, by the rider and by you. ChooseChow uses these
        delivery confirmation records to resolve disputes.
      </li>
    </ul>

    <h2>Allergies and food safety</h2>
    <p>
      Kitchens list ingredients and allergens themselves. That information may be incomplete, and
      home and market kitchens generally cannot guarantee separation between allergens.{' '}
      <strong>
        If you have a serious allergy, tell the kitchen directly through the order chat before you
        order, and treat an empty allergen list as unknown rather than as safe.
      </strong>{' '}
      Every kitchen on ChooseChow has passed our verification: identity checked, kitchen inspected by
      photo and video (and in person for some), and hygiene standards signed. Kitchens are re-verified
      every 6 months. That is a check, not a guarantee about any individual dish.
    </p>

    <h2>Hygiene reports</h2>
    <p>
      If food arrives undercooked, badly packaged, or makes you unwell, report it from the order in the
      app. A report pauses the kitchen straight away while our team reviews it. Report honestly: a false
      report harms a real person's business, and we may close accounts that make them.
    </p>

    <h2>Payment and refunds</h2>
    <ul>
      <li>
        <strong>Payment processing</strong> — Payments on ChooseChow are processed by Paystack. ChooseChow
        does not store your full card details. When you pay for an order, the payment is automatically
        divided between the chef and ChooseChow at checkout.
      </li>
      <li>
        <strong>When you can request a refund</strong> — You may request a full or partial refund if:
        your order was not delivered; the order was incorrect or incomplete; or the food was unsafe,
        spoiled, or significantly different from its description.
      </li>
      <li>
        <strong>How to request</strong> — Report the issue in the app or contact support at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> within 2 hours of delivery. Include
        photographs where relevant. Requests made after this 2-hour window may not be accepted.
      </li>
      <li>
        <strong>Review and timing</strong> — We review each request using the order record and information
        from you, the chef, and the rider. We aim to respond within 48 hours. Approved refunds are returned
        to your original payment method through Paystack or to your ChooseChow wallet, at your choice. Bank
        refunds typically take 5 to 10 business days to appear, depending on your bank.
      </li>
      <li>
        <strong>Non-refundable cases</strong> — Refunds are generally not available for change of mind
        after food preparation has started, wrong address or contact details given by you, or failed delivery
        caused by an unreachable customer.
      </li>
      <li>
        <strong>Chargebacks</strong> — Please contact ChooseChow support before filing a dispute with your
        bank so we can resolve the issue quickly. Chargebacks are handled through Paystack's dispute process.
      </li>
    </ul>

    <h2>If you sell on ChooseChow</h2>
    <ul>
      <li>
        <strong>Role and independence</strong> — You are an independent business, not an employee. You
        set your own menu, prices and operating hours. ChooseChow operates a marketplace and is not the seller
        of the food.
      </li>
      <li>
        <strong>Food safety responsibility</strong> — You are responsible for food safety, describing dishes
        accurately, and holding all licences and registrations required by your state.
      </li>
      <li>
        <strong>Verification and agreement acceptance</strong> — Before you can sell or receive payouts, you
        must complete verification and accept these terms. ChooseChow retains a timestamped record of your
        acceptance. Verification requires government ID, selfie, phone confirmation, kitchen photos and video,
        and signed hygiene standards, renewed every 6 months.
      </li>
      <li>
        <strong>Split at payment</strong> — When a customer pays for an order, the payment is automatically
        divided at checkout. The chef's share is allocated to your Paystack subaccount. ChooseChow's commission
        and applicable fees are allocated to ChooseChow. ChooseChow does not hold or control the chef's share.
      </li>
      <li>
        <strong>Settlement schedule</strong> — The chef's share is settled directly to your verified bank
        account on Paystack's standard settlement schedule, currently the next business day. Settlement timing
        may vary with bank processing times, public holidays, and Paystack operating schedules.
      </li>
      <li>
        <strong>Bank account details</strong> — You must provide a bank account in your own legal name,
        matching the identity verified during onboarding. You are responsible for keeping these details
        accurate. ChooseChow is not liable for settlements sent to incorrect details provided by the chef.
      </li>
      <li>
        <strong>Commission and fees</strong> — ChooseChow charges a commission on each order's food value,
        as shown in your chef dashboard. Commission rates may be changed with at least 30 days' written notice.
      </li>
      <li>
        <strong>Refunds, chargebacks, and recovery</strong> — If a refund or chargeback is approved for an
        order, the chef's share of that order may be recovered from future earnings by deduction from future
        settlements. If you have no future earnings to cover it, ChooseChow may invoice you for the amount,
        payable within 14 days. Where the cause is not the chef's fault (for example, a rider delivery error),
        ChooseChow bears the cost.
      </li>
      <li>
        <strong>Dispute response</strong> — When a customer raises a complaint, you must provide requested
        information, including photos, messages, and order details, within 48 hours. ChooseChow decides refund
        outcomes using the order record, including two-step delivery confirmation logs. Its decision on refunds
        is final, without limiting either party's statutory legal rights.
      </li>
      <li>
        <strong>Kitchen pauses and suspension</strong> — We may suspend a kitchen that repeatedly cancels
        orders, misdescribes food, breaches hygiene standards, or is the subject of credible safety complaints.
        If you are ill, pause your kitchen from the Verification page without penalty to your badge, reviews,
        or ranking.
      </li>
    </ul>

    <h2>What you may not do</h2>
    <ul>
      <li>Use ChooseChow for anything unlawful, or to sell anything other than food and groceries.</li>
      <li>Post reviews you did not earn, or reviews written to damage a competitor.</li>
      <li>Harass a kitchen, a rider, a customer or our staff.</li>
      <li>Scrape, resell or republish the catalogue, or try to break the platform's security.</li>
    </ul>

    <h2>Content you post</h2>
    <p>
      You keep ownership of the photographs, menu text and reviews you upload. You give ChooseChow a
      licence to display them on the platform and on the public kitchen and dish pages, including in
      search results and link previews, for as long as your content is live.
    </p>

    <h2>Liability</h2>
    <p>
      Kitchens are responsible for the food they cook. ChooseChow is responsible for operating the
      platform with reasonable skill and care. Nothing in these terms limits liability for death or
      personal injury caused by negligence, for fraud, or for anything else that cannot be limited
      under Nigerian law.
    </p>

    <h2>Ending your account</h2>
    <p>
      You can close your account at any time in the app. We may suspend or close an account that
      breaches these terms, and will tell you why unless we are legally prevented from doing so.
    </p>

    <h2>Governing law</h2>
    <p>
      These terms are governed by the laws of the Federal Republic of Nigeria, and disputes are
      subject to the jurisdiction of the Nigerian courts.
    </p>

    <h2>Changes</h2>
    <p>
      We will post any change here and update the date above. Where a change materially affects you
      we will tell you in the app before it takes effect. Continuing to use ChooseChow after that
      means you accept the new terms.
    </p>
  </LegalShell>
)

/**
 * Account and data deletion.
 *
 * Google Play requires two routes to deletion for any app that creates
 * accounts: one inside the app, and one at a public URL a person can reach
 * after uninstalling it. This is the second. It is deliberately not behind
 * RequireAuth — a page that demands a sign-in from someone who has lost access
 * to their account is not a deletion route.
 *
 * Both routes are real: Profile -> Delete account in the app, and the email
 * address below for anyone who has already uninstalled it. Both open the same
 * 30-day window (user.DeletionGracePeriod), and every retention period stated
 * on this page is the one the purge actually enforces — see
 * internal/service/user/deletion_repository.go. The two must not drift: Play
 * checks a declaration against real behaviour, and this is a promise to someone
 * who cannot verify it themselves.
 */
export const DeleteAccountPage = () => (
  <LegalShell>
    <h1>Delete your account and data</h1>
    <Updated>Last updated {LAST_UPDATED}</Updated>

    <p>
      You can ask us to delete your ChooseChow account, or to delete specific data without closing
      your account. This page explains how to make either request, what gets removed, and what we
      are required to keep.
    </p>

    <Callout>
      <p>
        <strong>In the app:</strong> open <strong>Profile → Delete account</strong>, confirm with
        your email address, and we schedule the deletion.
      </p>
      <p>
        <strong>If you have already uninstalled it:</strong> email{' '}
        <a href={`mailto:${CONTACT_EMAIL}?subject=Account%20deletion%20request`}>{CONTACT_EMAIL}</a>{' '}
        from the address on your account, with the subject{' '}
        <strong>Account deletion request</strong>. We confirm within 7 days.
      </p>
      <p>
        Either way your account is deleted <strong>30 days later</strong>. It keeps working during
        those 30 days, and you can cancel at any point before the date.
      </p>
    </Callout>

    <h2>Why we wait 30 days</h2>
    <p>
      Deletion cannot be undone, so we give you a month to change your mind. The delay also protects
      you: if someone else got into your account, a deletion they requested does not take effect
      before you have had a chance to notice it and cancel. Sign in at any time during the 30 days
      and choose <strong>Keep my account</strong>.
    </p>

    <h2>What to include</h2>
    <ul>
      <li>The email address on the account (send from it if you still can).</li>
      <li>The phone number on the account, so we can match it if the email has changed.</li>
      <li>
        Whether you want the <strong>whole account closed</strong>, or only{' '}
        <strong>specific data removed</strong> — and if so, which: saved addresses, dietary and
        allergy information, your profile photo, or your reviews.
      </li>
    </ul>
    <p>
      If we cannot match the request to an account we will ask you for one more identifying detail
      before we act. We do this because an unverified deletion request is a way to erase somebody
      else's account.
    </p>

    <h2>What we delete</h2>
    <ul>
      <li>Your name, email address, phone number and password hash.</li>
      <li>Your saved delivery addresses and their coordinates.</li>
      <li>Your cart, and anything left in it.</li>
      <li>Your dietary preferences and allergy information.</li>
      <li>Your profile photo.</li>
      <li>Your saved dishes, meal plans and past purchases.</li>
      <li>Your device and session records, and your ability to sign in.</li>
      <li>
        The text of messages you sent to a kitchen or a rider. The other side's replies are theirs,
        so those stay — but they no longer carry your name.
      </li>
      <li>
        The delivery addresses on your past orders. The orders themselves are kept (see below); the
        street address on them is not.
      </li>
      <li>
        If you sell on ChooseChow: your kitchen page, menu, dish photographs, opening hours and
        vendor locations are unpublished and removed.
      </li>
    </ul>

    <h2>What we keep, and for how long</h2>
    <p>
      Nigerian tax and company law requires us to retain records of completed transactions. We keep
      the following after an account is deleted, with your identifying details removed wherever the
      record still makes sense without them:
    </p>
    <ul>
      <li>
        <strong>Order and payment records</strong> — amount, date, kitchen, and payment reference.
        Retained for <strong>6 years</strong> from the end of the financial year in which the
        transaction happened, then deleted.
      </li>
      <li>
        <strong>Payout records</strong> for kitchens and riders, on the same 6-year basis.
      </li>
      <li>
        <strong>Records of a safety or fraud investigation</strong>, where one is open or concluded,
        for as long as we may need to answer for it. If none applies to you, we keep nothing under
        this heading.
      </li>
      <li>
        <strong>If you sold on ChooseChow: kitchen verification records</strong> — your ID and selfie,
        kitchen photos and video, certificate, signed hygiene standards, inspection records and hygiene
        reports. They are the evidence that we checked the kitchen before customers ate its food, so we
        keep them privately for 6 years or as required by law. They are never published.
      </li>
    </ul>
    <p>
      Reviews you wrote are anonymised rather than removed, because deleting them would rewrite
      other people's view of a kitchen's history. If you want a specific review taken down, say so
      in your request and we will remove it.
    </p>
    <p>
      Card and bank details are never stored by ChooseChow — they are handled by our payment processor
      (Paystack) — so there is nothing for us to delete there. Contact Paystack directly if you want their
      records removed.
    </p>

    <h2>Before you delete</h2>
    <ul>
      <li>
        <strong>Finish or cancel any open order.</strong> We will not schedule a deletion while food
        is being cooked or delivered — the app will tell you which orders are in the way.
      </li>
      <li>
        <strong>Withdraw your wallet balance.</strong> We will not schedule a deletion while money is
        still held in your account, because a deleted balance cannot be recovered.
      </li>
      <li>
        <strong>After the 30 days, deletion is permanent.</strong> There is no undo once the date has
        passed, and an account cannot be restored afterwards.
      </li>
    </ul>

    <h2>If you are not satisfied</h2>
    <p>
      The Nigeria Data Protection Act 2023 gives you the right to erasure. If we refuse a request we
      will tell you why and which exemption we are relying on. You can complain to the{' '}
      <strong>Nigeria Data Protection Commission</strong> about that decision. See our{' '}
      <Link to="/privacy">privacy policy</Link> for the full list of rights you hold.
    </p>
  </LegalShell>
)

/**
 * Public, ungated Kitchen Guidelines & Hygiene Standards.
 *
 * Prospective home chefs, customers, and health inspectors can view these
 * standards without logging in. Every selling kitchen agrees to uphold them.
 */
export const KitchenGuidelinesPage = () => (
  <LegalShell>
    <h1>Kitchen guidelines & hygiene standards</h1>
    <Updated>Last updated {LAST_UPDATED}</Updated>

    <p>
      ChooseChow connects customers with verified home chefs, food trucks, and local culinary makers.
      Every kitchen selling food on ChooseChow must satisfy verification and maintain these food safety
      and hygiene standards.
    </p>

    <Callout>
      <p>
        <strong>Our safety commitment.</strong> ChooseChow is built on trust and transparency. We verify
        every chef's identity, inspect their kitchen workspace, and review food safety standards before any
        kitchen can open for orders. Kitchens are re-verified every 6 months.
      </p>
    </Callout>

    <h2>1. The 6 core hygiene standards</h2>
    <p>
      Every chef on ChooseChow agrees to uphold the following standards during every meal preparation:
    </p>
    <ul>
      <li>
        <strong>Handwashing and utensil hygiene:</strong> Wash hands and utensils thoroughly before and during
        food preparation.
      </li>
      <li>
        <strong>Temperature control and separation:</strong> Store food at safe temperatures and keep raw and
        cooked food strictly separate.
      </li>
      <li>
        <strong>Illness and health policy:</strong> Do not prepare food while ill or carrying any infection
        that can spread. If you feel unwell, pause your kitchen immediately without penalty.
      </li>
      <li>
        <strong>Safe water:</strong> Use clean, potable water for cooking, washing, and sanitizing.
      </li>
      <li>
        <strong>Food-grade packaging:</strong> Package food only in clean, food-grade, leak-resistant
        containers.
      </li>
      <li>
        <strong>Inspection compliance:</strong> Accept that ChooseChow may inspect your kitchen, in person or
        by video, and may suspend your kitchen for a breach of these standards.
      </li>
    </ul>

    <h2>2. Kitchen onboarding & verification</h2>
    <p>
      Before a kitchen can list dishes or accept orders, our team reviews the following verification
      requirements:
    </p>
    <ul>
      <li>
        <strong>Identity verification:</strong> A valid government-issued ID (NIN slip or driver's licence)
        and a live selfie to confirm identity.
      </li>
      <li>
        <strong>Contact confirmation:</strong> An active Nigerian phone number verified by a one-time code
        or a phone call from our team.
      </li>
      <li>
        <strong>Kitchen inspection:</strong> Clear photographs and a video walkthrough of your cooking
        stations, food storage areas, and sanitation facilities.
      </li>
      <li>
        <strong>Hygiene agreement:</strong> Formal acceptance of our hygiene standards, recorded with a
        timestamped compliance record.
      </li>
      <li>
        <strong>Settlement account:</strong> A verified Nigerian bank account in your legal name to receive
        direct Paystack settlements.
      </li>
      <li>
        <strong>6-Month re-verification:</strong> Every 6 months, kitchens must submit fresh walkthrough
        videos and re-affirm the current hygiene standards.
      </li>
    </ul>

    <h2>3. Kitchen badge tiers</h2>
    <p>
      Customers can identify kitchen verification levels on dish cards and kitchen profiles:
    </p>
    <ul>
      <li>
        <strong>Verified Kitchen:</strong> Identity checked, phone verified, kitchen inspected by photo and
        video, and hygiene standards signed.
      </li>
      <li>
        <strong>Hygiene Certified:</strong> Earned by kitchens holding an accredited food-handler safety
        certificate from NAFDAC, a state health authority, or an approved food safety provider.
      </li>
      <li>
        <strong>Top Kitchen:</strong> Awarded to established kitchens active for at least 6 months with 10 or
        more customer ratings and a consistent 4.5+ star rating.
      </li>
    </ul>

    <h2>4. Delivery confirmation & customer reports</h2>
    <p>
      Deliveries are confirmed in two steps, by the rider and by the customer. These confirmation logs
      protect both kitchens and customers in resolving order disputes.
    </p>
    <p>
      If a customer reports a hygiene or food quality issue, our compliance team reviews the report promptly.
      Chefs are notified and must provide requested information within 48 hours. Sales may be paused while
      a hygiene report is reviewed to safeguard customer health.
    </p>

    <h2>5. Ready to sell on ChooseChow?</h2>
    <p>
      Turn your kitchen into a thriving food business. We handle order routing, customer payments, and rider
      delivery so you can focus on cooking extraordinary food.
    </p>
    <Callout>
      <p>
        <strong>Join our community of home chefs:</strong>{' '}
        <Link to="/register?role=chef">Apply to start your kitchen →</Link>
      </p>
    </Callout>
  </LegalShell>
)

